import classNames from 'classnames';
import { get, map, uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import Carousel, { Dots } from 'components/Carousel';
import NavigationArrow from 'components/HomepageV2Carousel/NavigationArrow';
import { ZoomCircleIcon } from 'components/Icon';
import IconButton from 'components/IconButton';
import ProductBadge, { PLACEMENTS } from 'components/ProductBadge';
import ResponsiveImage from 'components/ResponsiveImage';
import VideoPlayer from 'components/VideoPlayer';
import { IMAGE_BG_COLOR, IMAGE_RATIOS } from 'constants/images';
import { MEDIA_TYPES } from 'constants/media';
import { largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import { stringHash } from 'utils/Hash';
import I18n from 'utils/I18n';

import { IMAGE_SIZE_MAX } from './constants';
import PdpCarouselPlaceholder from './PdpCarouselPlaceholder';
import Thumbnails from './Thumbnails';

const PdpCarousel = ({
  isLoading,
  currentMainCarouselSlide,
  currentProductHandle,
  labeledModelImageIndex,
  modelDescription,
  setCurrentMainCarouselSlide,
  slides,
  handleZoomButtonClick,
}) => {
  const SLIDES_PER_PAGE = 1;
  const [isMouseOverHeroGallery, setIsMouseOverHeroGallery] = useState(false);
  const isLargeUp = useMediaQuery({ query: largeUp });

  const slidesCount = slides?.length ?? 0;
  const hasImages = !isLoading && slidesCount > 0;
  const hasMultipleImages = !isLoading && slidesCount > 1;

  const options = {
    draggable: hasMultipleImages,
    startIndex: 0,
    easing: 'ease-in-out',
    perPage: SLIDES_PER_PAGE,
  };

  const handleCarouselOnChange = useCallback(
    index => {
      // Only track if the index was updated from the Carousel e.g not programmatically elsewhere
      if (currentMainCarouselSlide !== index) {
        setCurrentMainCarouselSlide(index);
      }
    },
    [currentMainCarouselSlide, setCurrentMainCarouselSlide]
  );

  const handleHeroGalleryMouseEnter = () => {
    setIsMouseOverHeroGallery(true);
  };

  const handleHeroGalleryMouseLeave = () => {
    setIsMouseOverHeroGallery(false);
  };

  if (isLoading) {
    return (
      <div className="PdpCarouselWrapper">
        <PdpCarouselPlaceholder uniquekey="pdp-carousel-placeholder" slidesCount={slidesCount} />
      </div>
    );
  }

  if (!hasImages) return null;

  // generate unique key for all slides
  const carouselKeyPrefix = `main-carousel_${currentProductHandle}_`;
  const carouselKey = `${carouselKeyPrefix}${stringHash(JSON.stringify(slides.map(slide => get(slide, 'src', ''))))}`;

  return (
    <div className="PdpCarouselWrapper PdpCarouselWrapper__hero-gallery--thumbnails">
      <Thumbnails
        slides={slides}
        currentSlideIndex={currentMainCarouselSlide}
        setCurrentSlideIndex={setCurrentMainCarouselSlide}
        colorwayHandle={currentProductHandle}
      />

      <div
        className="PdpCarousel PdpCarousel--hero-gallery PdpCarousel--hero-gallery--thumbnails"
        onMouseEnter={handleHeroGalleryMouseEnter}
        onMouseLeave={handleHeroGalleryMouseLeave}
      >
        <Carousel
          id={uniqueId('pdp-carousel-')}
          className="PdpCarousel__main-carousel"
          currentSlide={currentMainCarouselSlide}
          onChange={handleCarouselOnChange}
          key={carouselKey}
          options={options}
        >
          {map(slides, ({ src, alt, poster, type, badge, showBadgeOnMobile }, i) => {
            // Because of outrageous video server bills, we only start loading the video if someone has expressed intent to watch it.
            const isFirstSlide = i === 0;
            const carouselHadInteraction = currentMainCarouselSlide !== 0;

            // Preload video if it's the first slide, or if someone has started scrolling the carousel
            const shouldPreloadVideo = isFirstSlide || carouselHadInteraction;

            // Video source is blank until we want to start preloading the video
            const videoSrc = shouldPreloadVideo ? src : null;

            const showBadge = badge && (showBadgeOnMobile || isLargeUp);

            return type === MEDIA_TYPES.IMAGE ? (
              <div
                className={classNames(['PdpCarousel__slide', 'PdpCarousel__slide--image'], {
                  'PdpCarousel__slide--last': i === slidesCount - 1,
                })}
                key={src}
              >
                <ResponsiveImage
                  src={src}
                  alt={alt}
                  width={IMAGE_SIZE_MAX}
                  height={IMAGE_SIZE_MAX}
                  transformations={{
                    backgroundColor: IMAGE_BG_COLOR,
                  }}
                  sizes="(min-width: 768px) 53vw, 100vw"
                />

                {showBadge && <ProductBadge badgeText={badge} placement={PLACEMENTS.TOP} />}
              </div>
            ) : (
              <div
                className={classNames(['PdpCarousel__slide', 'PdpCarousel__slide--video'], {
                  'PdpCarousel__slide--last': i === slidesCount - 1,
                })}
                key={src}
              >
                {videoSrc ? (
                  <VideoPlayer
                    url={videoSrc}
                    poster={poster}
                    playing
                    loop
                    controls={false}
                    muted
                    aspectRatio={IMAGE_RATIOS.R_1x1}
                    playsinline
                  />
                ) : (
                  <div className="PdpCarousel__slide--video-placeholder" />
                )}
              </div>
            );
          })}
        </Carousel>

        <div className="PdpCarousel__bottom">
          {hasMultipleImages && (
            <div className="PdpCarousel__dots">
              <Dots
                nDots={slidesCount}
                currentDot={Math.ceil(currentMainCarouselSlide)}
                onClick={setCurrentMainCarouselSlide}
              />
            </div>
          )}
          {!!modelDescription && labeledModelImageIndex === currentMainCarouselSlide && (
            <div className="PdpCarousel__model-description">{modelDescription}</div>
          )}
        </div>

        {hasMultipleImages && (
          <div className="PdpCarousel__buttons PdpCarousel__buttons--variation-thumbnails">
            <NavigationArrow
              aria-label={I18n.localize('carousel.viewPreviousSlide')}
              direction="left"
              onClick={() =>
                setCurrentMainCarouselSlide(
                  (Math.ceil(currentMainCarouselSlide) - 1 + slidesCount) % slidesCount
                )
              }
            />
            <NavigationArrow
              aria-label={I18n.localize('carousel.viewNextSlide')}
              direction="right"
              onClick={() =>
                setCurrentMainCarouselSlide((Math.ceil(currentMainCarouselSlide) + 1) % slidesCount)
              }
            />
          </div>
        )}

        <div
          className={classNames('PdpCarousel__zoom-icon-container', {
            'PdpCarousel__zoom-icon-container--active': isMouseOverHeroGallery,
          })}
        >
          <IconButton
            className="PdpCarousel__zoom-icon-button"
            onClick={() => handleZoomButtonClick(currentMainCarouselSlide)}
          >
            <ZoomCircleIcon className="PdpCarousel_zoom-icon" />
          </IconButton>
        </div>
      </div>
      <style jsx>{`
        .PdpCarousel {
          position: relative;
          text-align: center;
          overflow: hidden;
        }
        .PdpCarousel__bottom {
          position: absolute;
          z-index: 1;
          bottom: 0;
          left: 0;
          right: 0;
        }
        .PdpCarousel__dots {
          display: block;
          margin: ${Theme.get('spacing') * 4}px auto;
        }
        .PdpCarousel__slide {
          position: relative;
          margin: 0;
          width: 100%;
          background-color: ${Theme.get('colors-grey')};
        }
        .PdpCarousel--hero-gallery .PdpCarousel__slide {
          margin: 0;
          min-width: 100%;
        }
        .PdpCarousel__buttons {
          display: none;
        }

        .PdpCarousel__zoom-icon-container {
          display: none;
        }

        @media ${largeUp} {
          .PdpCarousel__dots {
            display: none;
          }

          .PdpCarouselWrapper__hero-gallery--thumbnails {
            display: flex;
            gap: 3.22%;
          }
          .PdpCarouselWrapper__hero-gallery--thumbnails > div {
            flex: 1;
            position: relative;
          }

          .PdpCarousel__buttons {
            position: absolute;
            bottom: ${Theme.get('spacing') * 4}px;
            z-index: 1;
            display: flex;
          }

          .PdpCarousel__buttons--variation-buttons {
            right: calc(15% + ${Theme.get('spacing') * 5}px);
          }

          .PdpCarousel__buttons--variation-thumbnails {
            right: ${Theme.get('spacing') * 5}px;
          }

          .PdpCarousel__zoom-icon-container {
            display: block;
            opacity: 0;
            position: absolute;
            bottom: ${Theme.get('spacing') * 4}px;
            left: ${Theme.get('spacing') * 5}px;
            z-index: 1;
            transition: opacity ${Theme.get('transition-timing-ease-in-out-sine')}
              ${Theme.get('transition-duration-fast-ms')};
          }

          .PdpCarousel__zoom-icon-container--active {
            opacity: 1;
          }
        }

        .PdpCarousel__model-description {
          padding-top: ${Theme.get('spacing') * 1.5}px;
          padding-bottom: ${Theme.get('spacing') * 1.5}px;
          background-color: ${Theme.get('colors-grey')};
          font-size: ${Theme.get('font-size-small-px')};
          font-weight: ${Theme.get('font-weight-semi-bold')};
          letter-spacing: 0.4px;
        }
      `}</style>
      <style jsx global>{`
        .PdpCarousel__slide--image .Image {
          max-width: 100%;
          display: block;
        }
        .PdpCarousel__buttons button + button {
          margin-left: ${Theme.get('spacing') * 2}px;
        }

        .PdpCarousel__buttons button.HomepageCarouselArrow {
          box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.15);
        }

        .PdpCarousel__slide--video-placeholder {
          padding-bottom: 100%;
        }

        .IconButton.PdpCarousel__zoom-icon-button {
          display: flex;
          justify-content: center;
          align-items: center;
          border: 1px solid ${Theme.get('colors-black-20')};
          width: ${Theme.get('spacing') * 8}px;
          height: ${Theme.get('spacing') * 8}px;
          border-radius: 50%;
          background-color: ${Theme.get('colors-white')};
          box-shadow: 0px 1px 6px 0px rgba(0, 0, 0, 0.15);
          transition: border-color ease-in-out ${Theme.get('transition-duration') / 2}ms;
        }

        .IconButton.PdpCarousel__zoom-icon-button:hover,
        .IconButton.PdpCarousel__zoom-icon-button.focused {
          border-color: ${Theme.get('colors-black-60')};
        }

        .IconButton.PdpCarousel__zoom-icon-button svg {
          width: 60%;
        }

        .PdpCarousel_zoom-icon.Icon {
          display: flex;
          justify-content: center;
          align-items: center;
        }
      `}</style>
    </div>
  );
};

PdpCarousel.propTypes = {
  slides: PropTypes.arrayOf(
    PropTypes.shape({
      alt: PropTypes.string,
      badge: PropTypes.string,
      poster: PropTypes.string,
      showBadgeOnMobile: PropTypes.bool,
      src: PropTypes.string.isRequired,
      type: PropTypes.string,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  currentMainCarouselSlide: PropTypes.number.isRequired,
  setCurrentMainCarouselSlide: PropTypes.func.isRequired,
  currentProductHandle: PropTypes.string.isRequired,
  labeledModelImageIndex: PropTypes.number,
  modelDescription: PropTypes.string,
  handleZoomButtonClick: PropTypes.func.isRequired,
};

PdpCarousel.defaultProps = {
  currentMainCarouselSlide: 0,
  setCurrentMainCarouselSlide: () => {},
  currentProductHandle: '',
  modelDescription: '',
  labeledModelImageIndex: -1,
};

export default PdpCarousel;
